<template>
  <div class="hub-editor layout-split">
    <template>
      <div class="side-panel side-panel-left subnav active"></div>
      <div class="layout-split-right">
        <div class="audience-main">
          <audience-header :all-contacts="false" :loading="isLoading || !segmentLoaded" class="pl-2" label="All Members" @reset="reset" :no-audience="!audiences.length && !isLoading && !hasFilter && ((segment && !segment.conditionsCount) || !segment)"> </audience-header>
          <b-dd class="mx-3 mt-3" id="status-dropdown" menu-class="status-selector" lazy bottom no-caret boundary="viewport" :variant="status ? 'secondary' : 'filter'">
            <template v-slot:button-content>
              <transition name="fade" mode="out-in">
                <div key="current" class="fx-row-center-between w-100">
                  <b class="txt-no-wrap">{{ statusLabel }}</b>
                  <b-btn class="ml-2" v-if="!!status" @click.stop="setStatus(null)" variant="primary" size="sm">
                    <svg width="24" viewBox="0 0 24 24" class="icon-sm"><use xlink:href="#icon-cross"></use></svg>
                  </b-btn>
                  <svg v-else class="btn-right" width="24" viewBox="0 0 24 24"><use xlink:href="#icon-chevron-down"></use></svg>
                </div>
              </transition>
            </template>
            <template>
              <b-dd-group class="status-selector-group">
                <b-dd-item v-for="item in statusOptions" :key="item.value" @click.stop.prevent="setStatus(item.value)" :active="status === item.value">
                  <svg class="icon-sm mr-2" v-if="item.icon" viewBox="0 0 24 24"><use :xlink:href="'#icon-' + item.icon"></use></svg>
                  <span class="no-min-width txt-no-wrap">{{ item.label }}</span>
                </b-dd-item>
              </b-dd-group>
            </template>
          </b-dd>
          <audience-table-head :all-contacts="false" v-if="hasFilter || (segment && segment.conditionsCount) || (audiences.length && !isRouteChanged) || isLoading" :count-total="total" :count-selected="selectedData.length" :loading="isLoading || !segmentLoaded" :empty="!audiences.length && !isLoading" @filter-changed="getAudienceApi" @selectAll="selectAll($event)" @sync="search(true)" />
          <div id="grid-wrapper" v-if="(audiences.length || isLoading) && !isRouteChanged">
            <div class="wrapper-lg px-2">
              <ag-grid-vue
                v-if="segmentLoaded"
                class="audience-table ag-table ph-no-capture"
                :class="{ disabled: isLoading }"
                :gridOptions="gridOptions"
                :columnDefs="columnDefs"
                :defaultColDef="defaultColDef"
                :frameworkComponents="frameworkComponents"
                :rowData="audiences"
                @sortChanged="sortChanged"
                @row-clicked="selectRow($event, 'Show')"
                @grid-ready="onGridReady"
                @grid-size-changed="onGridSizeChanged"
                @selection-changed="onSelectionChanged"
              />
            </div>
          </div>
          <transition name="fade" appear>
            <div v-if="isLoading || isRouteChanged" key="loading">
              <div class="wrapper-lg px-2">
                <div v-for="elem in 20" class="audience-skeleton">
                  <span class="skeleton-avatar"></span>
                  <div class="skeleton-lines">
                    <i class="skeleton-title"></i>
                    <i class="skeleton-subtitle"></i>
                  </div>
                </div>
              </div>
            </div>
            <div class="side-panel-group-empty txt-center" key="empty" v-else-if="!isLoading && !isRouteChanged && !audiences.length">
              <template v-if="hasFilter || (segment && segment.conditionsCount)">
                <illustration-dog-digging />
                <h4 class="txt-heading txt-bold mt-4">No Members Found</h4>
                <p class="txt-body txt-muted">We couldn’t find anyone matching your search. <br/>Try adjusting your filters or search terms.</p>
              </template>
              <template v-else-if="!hasFilter && !segment">
                <illustration-dog-bowl />
                <h4 class="txt-heading txt-bold mt-4 mb-2">No Members Yet</h4>
                <span class="txt-body txt-muted">Once people join your Hub, they’ll appear here.<br/>Add members to get started!</span>
                <b-btn variant="primary mt-4" @click.stop="openMemberDialog">
                  <svg viewBox="0 0 24 24" class="btn-left"><use xlink:href="#icon-user-add"></use></svg>
                  <b>Add Member</b>
                </b-btn>
              </template>
            </div>
          </transition>

          <div class="wrapper-lg">
            <div v-if="nextCursor !== undefined || prevCursor !== undefined" class="pagination" >
              <div class="pagination-inner w-100">
                <b-btn
                  variant="icon primary"
                  class="mx-0 mr-auto"
                  @click="goToCursor(prevCursor)"
                  :disabled="prevCursor === undefined"
                >
                  <svg viewBox="0 0 24 24" class="btn-left"><use xlink:href="#icon-arrow-left"></use></svg>
                  Previous Page
                </b-btn>
                <b-btn
                  variant="icon primary"
                  class="mx-0 ml-auto"
                  @click="goToCursor(nextCursor)"
                  :disabled="nextCursor === undefined"
                >
                  Next Page
                  <svg viewBox="0 0 24 24" class="btn-right"><use xlink:href="#icon-arrow-right"></use></svg>
                </b-btn>
              </div>
            </div>
          </div>
          <export-dialog />
          <segment-dialog />
          <delete-dialog v-if="audience" id="audience-delete-dialog" title="Delete Member" :processing="isDeleting" @accept="deleteAudience">
            <div class="txt-heading-small">
              Are you sure you want to delete <br /><b>{{ audience.name }}</b
              >?
            </div>
          </delete-dialog>
          <audience-segment-dialogs />
          <bulk-dialog />
          <exit-dialog id="exit-person-dialog" :static="false" :subtitle="exitDialogSubtitle" title="Save changes before exit?" description="Unsaved data will be lost." accept-label="Save & Exit" reject-label="Exit" @reject="rejectPersonSettingsExit" @accept="acceptPersonSettingsExit" />
          <exit-dialog id="audience-bulk-dialog" :static="false" title="Would you like to apply changes before leaving?" subtitle="" description="Unapplied changes will be lost." accept-label="Apply & Close" reject-label="Close" @reject="rejectAudienceBulk" @accept="acceptAudienceBulk">
            <template v-slot:subtitle><b class="txt-primary">Apply Edits?</b></template>
          </exit-dialog>
          <delete-dialog v-if="exportItem" id="export-delete-dialog" title="Delete Export" :resource-name="exportItem.title" :processing="isDeleting" @accept="deleteExport" />
          <delete-dialog v-if="selectedAudience" id="bulk-delete-dialog" title="Remove Members" :processing="isDeleting" @accept="deleteBulkAudience">
            <!-- <div class="txt-heading-small">Keep this the same as it currently is, no updates needed.</div> -->
            <div class="txt-heading-small">
              Are you sure you want to remove <b>{{ selectedAudience.length }} {{ selectedAudience.length > 1 ? 'members' : 'member' }}</b> from your Hub?
            </div>
          </delete-dialog>
          <delete-dialog v-if="selectedAudience" id="bulk-remove-dialog" title="Remove Members" :processing="isRemoving" @accept="removeBulkAudience" deleteButtonLabel="Remove">
            <div class="txt-heading-small">
              Are you sure you want to remove <b>{{ selectedAudience.length }} {{ selectedAudience.length > 1 ? 'members' : 'member' }}</b> from your Hub?
            </div>
          </delete-dialog>
          <rename-dialog v-if="exportItem" id="export-rename-dialog" title="Rename" v-model="exportItem.title" @accept="renameExport" :processing="renameProcessing" placeholder="Enter new title here" />
        </div>
      </div>
    </template>
    <transition name="fade" appear>
      <div v-if="!segmentLoaded" key="loading">
        <div class="wrapper-lg px-2 hub-editor-preview-loading w-100 mt-3 no-bg" style="margin-left: 320px; justify-content: flex-start;">
          <div v-for="elem in 20" class="audience-skeleton w-100">
            <span class="skeleton-avatar"></span>
            <div class="skeleton-lines">
              <i class="skeleton-title"></i>
              <i class="skeleton-subtitle"></i>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { AgGridVue } from 'ag-grid-vue';
import rootVue from '@app2/app';
import eventBus from '@app2/core/eventBus';
import grid from '@app2/modules/Audience/Helpers/grid';
import Pagination from '@app2/core/Components/Pagination';
import Segment from '@app2/models/Segment';

import personRouter from '@app2/modules/Audience/Person/person.router';

import get from 'lodash/get';
import capitalize from 'lodash/capitalize';
import AudienceHeader from '@app2/modules/Audience/Listing/Components/Header';
import SegmentDialog from '@app2/modules/Audience/Segment/App';
import ExportDialog from '@app2/modules/Audience/Export/App';
import BulkDialog from '@app2/modules/Audience/Bulk/App';
import DeleteDialog from '@app2/core/Components/Common/Modals/DeleteDialog';
import ExitDialog from '@app2/core/Components/Common/Modals/ExitDialog';
import RenameDialog from '@app2/core/Components/Common/Modals/RenameDialog';
import IllustrationDogBowl from '@illustrations/DogBowl';
import IllustrationDogDigging from '@illustrations/DogDigging';

import settingsCellRenderer from '@app2/modules/Audience/Helpers/settingsMemberCellRenderer';
import headerCellRenderer from '@app2/modules/Audience/Helpers/headerCellRenderer';
import statusCellRenderer from '@app2/modules/Audience/Helpers/statusCellRenderer';
import AudienceTableHead from '@app2/modules/Audience/Listing/Components/TableHead';
import AudienceSegmentDialogs from '@app2/modules/Audience/Segment/Components/Dialogs';
import router from '@app2/modules/Audience/Segment/segment.router';
import axios from '@app2/api/axios';
import Audience from '@app2/models/Audience';
import Hub from '@app2/models/Hub';

export default {
  name: 'Audience',
  metaInfo() {
    return {
      title: 'Audience',
    };
  },
  mixins: [grid],
  data() {
    return {
      frameworkComponents: null,
      defaultColDef: {
        editable: false,
        sortable: false,
        flex: 1,
        minWidth: 100,
        filter: true,
        resizable: false,
      },
      selectingAll: false,
      selectionChange: false,
      selectedData: [],
      gridApi: null,
      gridColumnApi: null,
      gridOptions: {
        rowSelection: 'multiple',
        suppressCellSelection: true,
        navigateToNextCell: this.navigateToNextCell,
        suppressRowClickSelection: true,
        headerHeight: 48,
        rowDeselection: true,
        rowHeight: 84,
        domLayout: 'autoHeight',
      },
      sdk: 'https://intercom.help/searchie/en/articles/5256393-track-audience-activity-on-your-website',
      hubHelpUrl: 'https://intercom.help/searchie/en/articles/5256429-invite-your-audience-to-your-private-hub',
      isDeleting: false,
      isRemoving: false,
      exportItem: null,
      renameProcessing: false,
      currentActivePage: null,
      isRouteChanged: false,
      redirect: 'https://help.membership.io/audience',
      segmentLoaded: false,
    };
  },
  components: {
    IllustrationDogBowl,
    IllustrationDogDigging,
    AudienceSegmentDialogs,
    AudienceTableHead,
    AudienceHeader,
    AgGridVue,
    Pagination,
    SegmentDialog,
    settingsCellRenderer,
    headerCellRenderer,
    statusCellRenderer,
    ExportDialog,
    DeleteDialog,
    BulkDialog,
    ExitDialog,
    RenameDialog,
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        this.getAudienceApi();
      },
    },
    columnDefs: {
      handler() {
        this.$nextTick(() => {
          if (this.gridApi) {
            this.gridApi.sizeColumnsToFit();
          }
        });
      },
      immediate: true
    }
  },
  computed: {
    activeHubHash() {
      return this.$store.getters['v2/activeHubHash'];
    },
    hub() {
      return Hub.find(this.activeHubHash);
    },
    statusLabel() {
      return this.status ? capitalize(this.status) : 'All Members';
    },
    columnDefs() {
      return [
        {
          headerName: '',
          checkboxSelection: true,
          headerCheckboxSelection: false,
          minWidth: 40,
          maxWidth: 40,
          // field: 'created_at',
          sortable: true,
          cellClass: 'pr-0',
        },
        {
          headerName: 'Name (ID)/Email',
          field: 'name',
          minWidth: 160,
          sortable: true,
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: { innerRenderer: this.userNameRenderer },
          cellClass: 'pl-0',
          flex: 1
        },
        {
          headerName: 'First seen',
          field: 'created_at',
          sortable: true,
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: { innerRenderer: this.seenRenderer },
          flex: 1
        },
        {
          headerName: 'Last seen',
          field: 'last_seen_at',
          sortable: true,
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: { innerRenderer: this.seenRenderer },
          flex: 1
        },
        {
          headerName: 'Status',
          field: 'subscription_status',
          width: 120,
          sortable: false,
          cellRendererFramework: 'statusCellRenderer',
        },
        {
          // headerComponentParams: { menuIcon: 'settings' },
          headerName: '',
          maxWidth: 80,
          sortable: false,
          editable: false,
          cellRendererFramework: 'settingsCellRenderer',
          cellRendererParams: {
            clicked: (data, toRoute) => {
              if (toRoute === 'Impersonate') {
                this.handleImpersonate(data);
                return;
              }
              if (toRoute === 'Remove') {
                this.handleRemove(data);
                return;
              }
              if (toRoute === 'SendWelcomeMail') {
                this.handleSendWelcomeMail(data);
                return;
              }
              if (toRoute === 'GenerateTemporaryPassword') {
                if (!this.hub || !data.data) return;
                const payload = {audienceHash: data.data.hash, hubHash: this.hub.hash};
                eventBus.$emit('show-temporary-password-dialog', payload);
                return;
              }
              if (toRoute !== 'toDelete') {
                this.selectRow(data.node, toRoute);
              } else {
                this.setToDeleteAudience(data);
              }
            },
          },
          type: 'rightAligned',
        },
      ];
    },
    statusOptions() {
      const options = [
        { label: 'Active', value: 'active', icon: 'status-success' },
        { label: 'Pending', value: 'pending', icon: 'clock' },
      ]
      if (this.hubHasPayments) {
        options.push({ label: 'Canceled', value: 'canceled', icon: 'status-disabled' });
      }
      return options;
    },
    hubHasPayments() {
      if (!this.hub || !this.hub.isPrivate) {
        return false;
      }
      return this.hub.hasPayments;;
    },
    segment() {
      return Segment.find(this.$store.state.v2.audience.segment.currentSegment);
    },
    ...mapGetters({
      page: 'v2/audience/page',
      status: 'v2/audience/status',
      prevCursor: 'v2/audience/prevCursor',
      nextCursor: 'v2/audience/nextCursor',
      total: 'v2/audience/total',
      lastPage: 'v2/audience/lastPage',
      hasFilter: 'v2/audience/hasFilter',
      isLoading: 'v2/audience/isLoading',
      audiences: 'v2/audience/getAudiences',
      filterModule: 'filter/filterModule',
      audience: 'v2/audience/selectedRowData',
      exitDialogSubtitle: 'v2/audience/exitDialogSubtitle',
      selectedAudience: 'v2/audience/selectedAudience',
      isAllSelected: 'v2/audience/isAllSelected',
    }),
  },
  beforeMount() {
    this.frameworkComponents = { agColumnHeader: 'headerCellRenderer' };
  },

  methods: {
    ...mapActions({
      search: 'v2/audience/segment/search',
    }),
    handleImpersonate(data) {
      this.$store.dispatch('v2/audience/impersonateAudience', { audienceHash: data.data.hash, hubHash: this.$route.params.hash });
    },
    async handleRemove(data) {
      const hub = Hub.find(this.$route.params.hash);
      await Audience.api().detachHubs(data.data.hash, { params: { hubs: [hub.id] } });
      // close show modal if opened
      this.$bvModal.hide('person-editor-dialog');
      setTimeout(async () => {
        await this.getAudienceApi();
        this.$toasted.show(`Removed "${data.data.name}" from hub!`);
      }, 500);
    },
    async handleSendWelcomeMail(data) {
      await this.$store.dispatch('v2/audience/sendWelcomeEmail', { audienceHash: data.data.hash, hubHash: this.$route.params.hash });
      this.$toasted.show(`Welcome email sent for "${data.data.name}"!`);
    },
    openMemberDialog() {
      rootVue.$bvModal.show('add-member-modal');
    },
    toggleBodyClass(addRemoveClass, className) {
      let el = document.body;
      if (addRemoveClass === 'addClass') {
        el.classList.add(className);
      } else {
        el.classList.remove(className);
      }
    },
    selectAll(bool) {
      this.selectingAll = true;
      if (bool) {
        this.gridOptions.api.selectAll();
      } else {
        this.selectionChange = false;
        if (!this.selectionChange) {
          this.gridOptions.api?.deselectAll();
        }
      }
      this.$store.commit('v2/audience/SET_SELECT_ALL', bool);
    },
    reset() {
      this.$store.commit('v2/audience/segment/SET_SEGMENT', null);
      this.$store.commit('v2/audience/segment/SET_PREVIEW', null);
      this.$store.commit('v2/audience/SET_PAGE', 1);
      this.$store.commit('v2/audience/RESET_CURSORS')
      this.$store.commit('v2/audience/segment/SET_SHOW_FILTERS', false);
      this.$store.commit('v2/audience/segment/SET_WITH_PARAMS', false);
      Segment.deleteAll();
      this.$store.dispatch('v2/audience/segment/resetNewSegment');
      router.push({ name: 'Create' }).catch(() => {});
      this.$nextTick(() => {
        this.$store.dispatch('v2/audience/getAudiences');
      });
    },
    setToDeleteAudience(params) {
      this.$store.dispatch('v2/audience/setSelectedRowData', params.data);
      setTimeout(() => {
        rootVue.$bvModal.show('audience-delete-dialog');
      }, 200);
    },
    deleteAudience() {
      this.isDeleting = true;
      axios
        .delete(`/api/audience/${this.audience.hash}`)
        .then(() => {
          this.$toasted.show('User Removed from Contacts.', { type: 'success' });
          this.$store.commit('v2/audience/REMOVE_PERSON', this.audience);
          this.$store.commit('v2/audience/person/SET_MODAL_STATE', false);
          this.isDeleting = false;
          rootVue.$bvModal.hide('audience-delete-dialog');
          this.clearFocus();
        })
        .catch(() => {
          this.$toasted.show('Error while saving changes', { type: 'danger' });
        });
    },
    async removeBulkAudience() {
      this.isRemoving = true;
      const hub = Hub.find(this.$route.params.hash);
      const audiences = this.selectedAudience.map(d => d.id);
      try {
        await axios.put(`/api/internal/v2/hubs/${hub.hash}/bulk-detach`, { audience_ids: audiences });
      } catch (e) {
        this.$toasted.show('Error while removing members from hub', { type: 'danger' });
        this.isRemoving = false;
        return;
      }
      this.isRemoving = false;
      this.$store.commit('v2/audience/SET_SELECTED_AUDIENCE', []);
      this.selectedData = [];
      this.$store.commit('v2/audience/SET_SELECT_ALL', false);
      this.$toasted.show(`${audiences.length} ${audiences.length > 1 ? 'members' : 'member'} removed from hub!`, { type: 'success' });
      setTimeout(() => {
        this.getAudienceApi();
      }, 500);
      rootVue.$bvModal.hide('bulk-remove-dialog');
    },  
    async deleteExport() {
      this.isDeleting = true;
      try {
        await axios.delete(`/api/audience/exports/${this.exportItem.hash}`);
        this.$parent.$forceUpdate();
        this.$toasted.show('Export deleted successfully!', { type: 'success' });
        rootVue.$bvModal.hide('export-delete-dialog');

        this.isDeleting = false;
        eventBus.$emit('delete-export-success', this.exportItem);
      } catch (e) {
        this.$toasted.show('Could not delete export, something went wrong. :(', { type: 'error' });
        throw e;
      }
    },
    deleteBulkAudience() {
      this.isDeleting = true;
      const audiences = this.selectedAudience.map(d => d.id);
      axios
        .delete(`/api/audience/bulk`, { params: { audiences } })
        .then(() => {
          this.$store.commit('v2/audience/UPDATE_TOTAL_COUNT');
          this.$store.commit('v2/audience/REMOVE_AUDIENCE', audiences);
          rootVue.$bvModal.hide('bulk-delete-dialog');
          this.$toasted.show(`${audiences.length} ${audiences.length > 1 ? 'people' : 'person'} removed from Contacts.`, { type: 'success' });
          this.isDeleting = false;
          if (this.$route.query.page) {
            if (parseInt(this.$route.query.page) === this.lastPage && (this.selectedAudience.length === 25 || this.isAllSelected)) {
              let currentActivePage = parseInt(this.$route.query.page) - 1;
              this.$store.commit('v2/audience/SET_PAGE', currentActivePage);
            }
          }
          this.$store.commit('v2/audience/SET_SELECTED_AUDIENCE', []);
          this.selectedData = [];
          this.$store.commit('v2/audience/SET_SELECT_ALL', false);
          setTimeout(() => {
            this.getAudienceApi();
          }, 500);
        })
        .catch(e => {
          this.$toasted.show('Error while saving changes', { type: 'error' });
        });
    },
    async renameExport() {
      this.renameProcessing = true;
      try {
        await axios.put(`/api/audience/exports/${this.exportItem.hash}`, this.exportItem);
        this.$toasted.show('Export renamed successfully!', { type: 'success' });
        rootVue.$bvModal.hide('export-rename-dialog');
        this.renameProcessing = false;
        eventBus.$emit('rename-export-success', this.exportItem);
      } catch (e) {
        this.$toasted.show('Could not delete export, something went wrong. :(', { type: 'error' });
        throw e;
      }
    },
    selectRow(params, toRoute) {
      personRouter.push({ path: '/' }).catch(() => {}); // re-trigger mounted for activity
      this.$nextTick(() => {
        this.$store.commit('v2/audience/SET_FROM_HOME', true);
        this.$store.dispatch('v2/audience/getAudienceAnalytics', params.data.hash);
        this.$store.dispatch('v2/audience/getAudienceActivity', params.data.hash);
        this.$store.dispatch('v2/audience/getAudienceAttributes', params.data.hash);
        this.$store.dispatch('v2/audience/getAudienceAchievements', params.data.hash);
        this.$store.dispatch('v2/audience/getAudienceHubAccess', params.data.hash);
        this.$store.dispatch('v2/audience/setSelectedRowData', params.data);
        personRouter.push({ name: toRoute, params: { person: params.data, hub: this.$route.params.hash } }).catch(() => {});
        rootVue.$bvModal.show('person-editor-dialog');
      });
    },
    goToCursor(cursor) {
      this.$store.commit('v2/audience/SET_CURSOR', cursor)
      this.$store.dispatch('v2/audience/getAudiences')
      this.$store.commit('v2/audience/SET_SELECT_ALL', false);
      this.selectedData = [];
    },
    setStatus(status) {
      this.$store.commit('v2/audience/SET_STATUS', status)
      this.$store.commit('v2/audience/RESET_CURSORS')
      this.$store.commit('v2/audience/SET_SELECT_ALL', false);
      this.selectedData = [];
      this.$store.dispatch('v2/audience/getAudiences');
    },
    currentPage(page) {
      this.$store.commit('v2/audience/SET_PAGE', page);
      this.$store.dispatch('v2/audience/getAudiences');
      this.$store.commit('v2/audience/SET_SELECT_ALL', false);
      this.selectedData = [];
    },
    onSelectionChanged(e) {
      this.selectedData = this.gridApi.getSelectedRows();
      this.$store.commit('v2/audience/SET_SELECTED_AUDIENCE', this.selectedData);
      if (!this.selectingAll) {
        this.selectionChange = true;
        this.$store.commit('v2/audience/SET_SELECT_ALL', false);
      }
      this.selectingAll = false;
      this.clearFocus();
    },
    async getAudienceApi() {
      if (!Object.keys(this.$route.query).length) {
        this.$store.commit('v2/audience/RESET_QUERY_PARAMS');
        this.$store.commit('v2/audience/RESET_CURSORS')
      }
      await this.$store.dispatch('v2/audience/getAudiences');
      this.$store.dispatch('v2/audience/getHubs');
    },
    async initialSearch(advanced = false) {
      if (advanced) {
        this.gridOptions.rowHeight = 84;
      } else {
        if (this.$route.query.page) this.$store.commit('v2/audience/SET_PAGE', +this.$route.query.page);
        if (Object.keys(this.$route.query).length !== 0) {
          if (this.$route.query.controller !== 'created_before' || this.$route.query.controller !== 'created_after' || this.$route.query.controller !== 'created_between') {
            this.$store.commit('v2/audience/SET_FILTER_QUERY_PARAMS', this.$route.query[`filter[${this.$route.query.controller}]`]);
            this.$store.commit('v2/audience/RESET_CURSORS')
          }
          this.$store.dispatch('v2/audience/setAudienceTypeQueryParams', this.$route.query.isPerson);
          this.$store.commit('v2/audience/SET_PAGE', this.$route.query.page);

          if (this.$route.query.controller) {
            this.$store.commit('filter/SET_CONTROLLER_NAME', this.$route.query.controller);
          } else {
            this.$store.commit('filter/SET_CONTROLLER_NAME', 'email');
          }
        } else {
          this.$store.commit('filter/SET_CONTROLLER_NAME', 'email');
          this.$store.commit('v2/audience/SET_PAGE', 1);
        }
        this.gridOptions.rowHeight = 84;
        await this.getAudienceApi();
      }
    },
    updateRow(data) {
      let index = this.audiences.findIndex(a => a.id == data.id);
      let rowNode = this.gridApi.getRowNode(index);
      rowNode.setData(data);
      this.gridApi.refreshCells({ force: true, rowNodes: [rowNode] });
    },
    acceptPersonSettingsExit() {
      this.$store.commit('v2/audience/person/SET_CAN_CLOSE_MODAL', true);
      this.$store.commit('v2/audience/person/SET_MODAL_STATE', false);
      eventBus.$emit('save-profile-settings');
      rootVue.$bvModal.hide('exit-person-dialog');
    },
    rejectPersonSettingsExit() {
      this.$store.commit('v2/audience/person/SET_CAN_CLOSE_MODAL', true);
      this.$store.commit('v2/audience/person/SET_MODAL_STATE', false);
      eventBus.$emit('reset-profile-settings');
      rootVue.$bvModal.hide('exit-person-dialog');
    },
    rejectAudienceBulk() {
      eventBus.$emit('reset-bulk-edit-settings');
      rootVue.$bvModal.hide('audience-bulk-dialog');
    },
    acceptAudienceBulk() {
      eventBus.$emit('save-bulk-edit-settings');
      rootVue.$bvModal.hide('audience-bulk-dialog');
    },
    clearFocus() {
      this.gridApi.clearFocusedCell();
    },
    async handleMemberAdded() {
      await this.getAudienceApi();
      this.$toasted.show('Members successfully added.', { type: 'success' });
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.$route.params.hash) {
      localStorage.removeItem(this.$route.params.hash);
    }
    this.isRouteChanged = true;
    this.$store.dispatch('v2/audience/segment/resetNewSegment', true);
    this.$store.commit('v2/audience/segment/SET_SEGMENT', null);
    this.$store.commit('v2/audience/segment/SET_PREVIEW', null);
    this.$store.commit('v2/audience/UPDATE_LOADING_STATE', false);
    this.$store.commit('v2/audience/UNSET_AUDIENCES');
    this.$store.commit('v2/audience/UNSET_SELECTED_ROW_DATA');
    this.$store.commit('v2/audience/RESET_QUERY_PARAMS');
    this.$store.commit('v2/audience/SET_PAGE', 1);
    this.$store.commit('v2/audience/RESET_CURSORS')
    this.$store.commit('v2/audience/segment/SET_SHOW_FILTERS', false);
    this.$store.commit('v2/audience/segment/SET_ATTRIBUTES', []);
    next();
  },
  async mounted() {
    this.$nextTick(() => {
      eventBus.$on('update-audience', data => {
        this.updateRow(data);
      });
      eventBus.$on('close-person-dialog', () => {
        this.clearFocus();
      });
      eventBus.$on('set-export-item', data => {
        this.exportItem = JSON.parse(JSON.stringify(data));
      });
      eventBus.$on('remove-hub-access', hubs => {
        hubs.forEach(hub => {
          this.selectedData.forEach(audience => {
            audience.hubs = audience.hubs.filter(h => h.id !== hub.id);

            this.updateRow(audience);
          });
        });
      });

      eventBus.$on('send-welcome-mail', this.handleSendWelcomeMail);
      eventBus.$on('impersonate', this.handleImpersonate);
      eventBus.$on('remove', this.handleRemove);
      eventBus.$on('refresh-members', this.handleMemberAdded);
    });
    if (this.$route.params.hash) {
      this.segmentLoaded = true;
      this.$store.dispatch('v2/audience/getAudiences');
      return;
    }
    this.$store.commit('v2/audience/UPDATE_LOADING_STATE', true);
    if (this.$route.params.hash) {
      eventBus.$emit('toggleOverlay', false);
      this.$store.commit('v2/audience/segment/SET_WITH_PARAMS', true);
      const hubId = localStorage.getItem(this.$route.params.hash);
      await this.$store.dispatch('v2/audience/segment/resetNewSegment', false);
      if (!!hubId) {
        await this.$store.dispatch('v2/audience/segment/newSegment', {
          hub: parseInt(hubId),
          bypass: true,
        });
      } else {
        const { data } = await axios.get(`/api/internal/v2/hubs/${this.$route.params.hash}/`);
        await this.$store.dispatch('v2/audience/segment/newSegment', {
          hub: parseInt(data.id),
          bypass: true,
        });
      }
      await this.$store.dispatch('v2/audience/segment/setPreview', true);
      this.$store.commit('v2/audience/segment/SET_SHOW_FILTERS', true);
      this.segmentLoaded = true;
    }
    if (this.$route.query.tag) {
      this.$store.commit('v2/audience/segment/SET_WITH_PARAMS', true);
      await this.$store.dispatch('v2/audience/segment/newSegment', {
        tags: [this.$route.query.tag],
      });
      await this.$store.dispatch('v2/audience/segment/setPreview', true);
      this.$store.commit('v2/audience/segment/SET_SHOW_FILTERS', true);
    }
    this.$nextTick(async () => {
      await this.initialSearch(get(this.$route, 'params.advanced', false));

      if (this.$route.params && this.$route.params.toRoute) {
        const formatPerson = {
          data: { ...this.$route.params.person },
        };
        this.selectRow(formatPerson, this.$route.params.toRoute);
      }
    });
    this.toggleBodyClass('addClass', 'audience-body');
  },
  beforeDestroy() {
    eventBus.$off('refresh-members', this.handleMemberAdded);
    eventBus.$off('send-welcome-mail', this.handleSendWelcomeMail);
    eventBus.$off('impersonate', this.handleImpersonate);
    eventBus.$off('remove', this.handleRemove);
  },
  destroyed() {
    this.toggleBodyClass('removeClass', 'audience-body');
    this.$store.commit('v2/audience/SET_SELECT_ALL', false);
  },
};
</script>

<style lang="scss">
.status-selector {
  width: 200px;
}
#status-dropdown .dropdown-toggle {
  min-width: 150px;
  min-height: 50px;
}
</style>